import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { PendingTask, registerPendingTaskDescBuilder } from './pending-task.js';

export const SET_EXAM_METADATA_TASK_CLASS = 'SetExamMetadataPendingTask';

/** Options stored on the pending metadata task. */
export interface SetExamMetadataTaskOptions {
  subject: string;
  topic: string;
  /** The grade/level already known (e.g. "12"), if any — absent means neither grade nor suffix was found. */
  grade?: string;
  /** False when the structure extraction couldn't fully determine the class (grade AND suffix both
   * present) — the class stays open on this same task until `set_metadata` resolves it with `klasse`. */
  classResolved: boolean;
}

/**
 * Pending task created after the split agent processes the exam sheet.
 * Instructs the main agent to call set_metadata with the extracted subject/topic — always resolvable
 * (subject falls back to "other"), so that part never needs to ask the user anything.
 *
 * Also carries the class/grade question when the structure extraction couldn't fully determine it
 * (grade AND sub-class/suffix both present) — see `queueExamMetadataTask` in structure-step.ts. The
 * task stays in `examState.pendingTasks` (see `metadataAvailability` in metadata.tool.ts) until BOTH
 * subject/topic AND class (when it was unresolved) are set — `classResolved` tracks the latter
 * independently of `examState.metaDataSet`, which only tracks fach/thema.
 *
 * The question's wording is NOT left to the LLM (it ended up buried in longer replies): it is built
 * by db/utils/exam-class-question.ts and appended to the agent's reply to the STRUCTURE confirmation —
 * the seam where the guided upload sequence ends and the rest of the pipeline is waiting on this very
 * answer (see `examClassPending` in jobs/job-conditions.ts). `askedAt`/`askCount` are the claim that
 * keeps it to exactly one ask: null means "not asked yet", and a stamped value blocks further asks for
 * a day, so two turns racing each other cannot both append it.
 */
@Entity()
export class SetExamMetadataPendingTask extends PendingTask {
  static override _class = SET_EXAM_METADATA_TASK_CLASS;

  @Field(() => String)
  subject!: string;

  @Field(() => String)
  topic!: string;

  @Field(() => String)
  grade: Maybe<string>;

  @Field(() => Boolean)
  classResolved!: boolean;

  @Field(() => String)
  priority: Maybe<string>;

  /** ISO timestamp of when the class question last actually reached the conversation — null until
   * then, which is also its state right after creation: the structure pipeline queues this task
   * without asking (see `queueExamMetadataTask`), because the teacher is still being walked through
   * the upload sequence. A null value therefore means "still to ask, cooldown does not apply". */
  @Field(() => String)
  askedAt: Maybe<string>;

  /** How many times the class question has actually been posted so far. */
  @Field(() => Int)
  askCount: Maybe<number>;
}

/** Builds the raw document pushed to examState.pendingTasks in MongoDB. */
export function createSetExamMetadataPendingTask(options: SetExamMetadataTaskOptions): Record<string, unknown> {
  return {
    _class: SET_EXAM_METADATA_TASK_CLASS,
    subject: options.subject,
    topic: options.topic,
    grade: options.grade ?? null,
    classResolved: options.classResolved,
    // 'high': easy to skim past, must be worked into the current reply, but nothing needs to
    // auto-restart while it waits for an answer, so not 'workflow'.
    priority: 'high',
    // Left unset here — `claimExamClassAsk` stamps it once the question actually reaches the teacher
    // (see the field doc above). Leaving it unset, rather than "now", is what lets the first eligible
    // agent turn ask immediately instead of waiting out a cooldown that never started.
    askedAt: null,
    askCount: 0,
  };
}

// Register the desc builder for this task type (side-effect on import).
registerPendingTaskDescBuilder(SET_EXAM_METADATA_TASK_CLASS, task => {
  const t = task as unknown as Record<string, unknown>;
  const subject = String(t.subject ?? '');
  const topic = String(t.topic ?? '');
  const classResolved = t.classResolved !== false;

  if (classResolved) {
    return `### PENDING TASK: Klausurmetadaten setzen [set_metadata]
Vom Klausurblatt wurden folgende Metadaten automatisch extrahiert:
- Fach: ${subject}
- Thema: ${topic}

Erforderliche Aktion: Rufe sofort set_metadata mit fach="${subject}" und thema="${topic}" auf. Teile dem Nutzer im Tool-Log mit, dass die Metadaten gesetzt wurden.
Passe das Thema der Sprache des Nutzers an. Übernimm das Fach direkt.`;
  }

  const grade = typeof t.grade === 'string' && t.grade.trim() ? t.grade.trim() : null;
  const knownLine = grade
    ? `- Fach: ${subject}\n- Klasse/Stufe: ${grade} (bereits bekannt — es fehlt nur noch der Kurs/Buchstabe, z.B. a, b, c, LK, GK)`
    : `- Fach: ${subject}\n- Klasse/Stufe: [NOCH UNBEKANNT]`;

  return `### PENDING TASK: Klausurmetadaten setzen, Klasse vervollständigen [set_metadata]
Vom Klausurblatt wurden folgende Metadaten automatisch extrahiert:
- Fach: ${subject}
- Thema: ${topic}

Rufe sofort set_metadata mit fach="${subject}" und thema="${topic}" auf (ohne klasse), falls das noch nicht geschehen ist.

Zusätzlich fehlt noch die Klasse:
${knownLine}
WICHTIG: Solange die Klasse fehlt, PAUSIERT der weitere Ablauf — Musterlösung, Bewertungsschema und die Auswertung der Schülerabgaben starten erst, wenn der Nutzer die Klasse genannt hat. Sag ihm das in deiner Antwort nach der Struktur-Bestätigung klar, aber in einem Satz.
Die Frage nach der Klasse/Kursbezeichnung selbst wird automatisch an deine Antwort angehängt — formuliere sie NICHT selbst aus, auch nicht in anderen Worten, sonst steht sie doppelt im Chat.

Erforderliche Aktion für die Klasse: Warte auf die Antwort des Nutzers. Rufe set_metadata NICHT erneut mit klasse auf, solange keine Antwort auf die Klassenfrage vorliegt. Sobald der Nutzer antwortet (z.B. "a", "LK", "9a", "GK 11"), kombiniere seine Antwort mit dem bereits bekannten Teil (falls vorhanden) zu einer vollständigen Klassenbezeichnung — z.B. bekannt "12" + Antwort "LK" -> "LK 12"; nichts bekannt + Antwort "9a" -> "9a" — und rufe set_metadata mit fach="${subject}", thema="${topic}" und klasse="<vollständige Bezeichnung>" auf.`;
});
